import { create } from 'zustand';
import { Permissions } from "ts-interfaces";
import { getBoardsScheme, renameBoard as renameBoardApi, copyBoard, deleteBoard as deleteBoardApi } from '../v2/apis/boardsSchemeApi';
import { activeTeamScope, awaitActiveTeamScope, teamScopeKnown } from '../v2/apis/accessQueries';
import { onSharingV2Flip } from 'ui/utils/workspace/sharingV2';
import { BoardSchemeInterface } from 'ts-interfaces';
import { docBelongsToActiveWorkspace, getActiveDesignFirmId, useWorkspaceStore } from 'ui';

/**
 * Workspace ownership check for a board (Phase A.3.2).
 *
 * Personal workspace: only boards where design_firm_id is null/absent.
 * Team workspace: only boards where design_firm_id === active firm id.
 * Boards created before the workspace migration have no design_firm_id —
 * they default to Personal.
 *
 * Thin wrapper over the shared `docBelongsToActiveWorkspace` so the predicate
 * lives in one place; kept as a named export for call sites and tests.
 */
export function boardBelongsToActiveWorkspace(
  board: { design_firm_id?: string | null },
  userId: string | null,
): boolean {
  return docBelongsToActiveWorkspace(board, userId);
}

/**
 * Derive userBoards / sharedBoards / activeBoards (active-only) plus the
 * archived complement from the raw board cache, keyed on the active workspace.
 *
 * Archiving is a soft-delete (`archive: true`), so archived boards live in the
 * same cache as active ones. We split them here rather than reading a URL
 * param, and surface `archivedUserBoards` so the UI can offer the opt-in
 * archived recovery view. `archivedSharedBoards` is owned by
 * `refreshSharedBoards` (the only place the shared cache is fetched) and is
 * intentionally not returned here so a board re-derive doesn't clobber it.
 *
 * - Team workspace: every board stamped with the active firm id (the fetch
 *   already constrained to design_firm_id == firmId; the predicate is the
 *   defensive belt) — regardless of `board.user`, so teammates' boards show.
 *   ACL-shared boards are personal-context and are excluded from the team view.
 *   Archived boards are the exception: they stay own-only, because restore
 *   authorization (`hasAccessToResource`) is owner/ACL-based with no firm
 *   awareness — surfacing a teammate's archived board would offer a Restore
 *   that always fails.
 * - Personal workspace: unchanged — own boards in the personal workspace
 *   (design_firm_id null) plus ACL-shared boards.
 */
function deriveBoardLists(
  boards: BoardSchemeInterface[],
  state: { currentUserId: string | null; sharedBoards: BoardSchemeInterface[] },
) {
  const teamFirmId = getActiveDesignFirmId(state.currentUserId);
  const activeShared = state.sharedBoards.filter((b) => !b.archive);

  if (teamFirmId) {
    const workspaceBoards = boards.filter((b) =>
      boardBelongsToActiveWorkspace(b, state.currentUserId),
    );
    const userBoards = workspaceBoards.filter((b) => !b.archive);
    const archivedUserBoards = workspaceBoards.filter(
      (b) => b.archive && b.user === state.currentUserId,
    );
    return {
      userBoards,
      sharedBoards: activeShared,
      activeBoards: userBoards,
      archivedUserBoards,
    };
  }

  const workspaceUserBoards = boards
    .filter((b) => b.user === state.currentUserId)
    .filter((b) => boardBelongsToActiveWorkspace(b, state.currentUserId));
  const userBoards = workspaceUserBoards.filter((b) => !b.archive);
  const archivedUserBoards = workspaceUserBoards.filter((b) => b.archive);
  return {
    userBoards,
    sharedBoards: activeShared,
    activeBoards: [...userBoards, ...activeShared],
    archivedUserBoards,
  };
}

interface BoardsState {
  boards: BoardSchemeInterface[];
  userBoards: BoardSchemeInterface[];
  sharedBoards: BoardSchemeInterface[];
  activeBoards: BoardSchemeInterface[];
  archivedUserBoards: BoardSchemeInterface[];
  archivedSharedBoards: BoardSchemeInterface[];
  isLoading: boolean;
  lastDoc: any;
  hasMore: boolean;
  permissionsRef: Permissions | null;
  initialFetchDone: boolean;
  sharedFetchDone: boolean;
  currentUserId: string | null;
  sharedBoardIds: string[];

  setBoards: (boards: BoardSchemeInterface[]) => void;
  getNext: (firstPageOnly?: boolean, pageSize?: number) => void;
  getBoards: (userId: string, permissions: Permissions | null, pageSize?: number) => {
    boards: BoardSchemeInterface[];
    setBoards: (boards: BoardSchemeInterface[]) => void;
    getNext: () => void;
    isLoading: boolean;
    hasMore: boolean;
  };
  getBoardsOptimized: (userId: string, permissions: Permissions | null, pageSize?: number) => {
    boards: BoardSchemeInterface[];
    setBoards: (boards: BoardSchemeInterface[]) => void;
    getNext: () => void;
    isLoading: boolean;
    hasMore: boolean;
  };
  refreshSharedBoards: (permissions: Permissions) => Promise<void>;
  syncBoardAssociations: (boardsToAdd: string[], boardsToRemove: string[], projectId: string) => void;
  /**
   * Merge `patch` into one cached board wherever it lives (every list), so a
   * field-level write elsewhere (a tray send) shows up without a refetch.
   * No-op when the board isn't cached.
   */
  patchBoard: (boardId: string, patch: Partial<BoardSchemeInterface> & Record<string, unknown>) => void;
  /** Drop a board from every cached list (after a permanent delete). */
  removeBoard: (boardId: string) => void;
  resetBoards: () => void;
}

// Bumped by every getNext so an in-flight page loop can tell it was superseded.
let fetchGeneration = 0;
// The ACL-shared fetch a flagged team skipped (refreshSharedBoards), with the
// asking user and the latest permissions; leaving the team runs it.
let skippedShared: { uid: string | null; permissions: Permissions } | null = null;

// Shared boards asked for while a first page is in flight, tagged with the
// asking user. The SDK resolves getDocs on its listen stream together, so a
// slow shared query held page 1.
let pendingShared: { uid: string | null; permissions: Permissions } | null = null;

function refreshSharedAfterFirstPage(permissions: Permissions) {
  const { isLoading, lastDoc, currentUserId, refreshSharedBoards } = useBoards.getState();
  if (isLoading && !lastDoc) {
    pendingShared = { uid: currentUserId, permissions };
    return;
  }
  refreshSharedBoards(permissions);
}

function flushPendingShared() {
  const pending = pendingShared;
  pendingShared = null;
  // Never apply another user's permissions to this store.
  if (pending && pending.uid === useBoards.getState().currentUserId) {
    useBoards.getState().refreshSharedBoards(pending.permissions);
  }
}

/** Board ids shared with the user through their ACL permissions. */
const sharedBoardIdsOf = (permissions: Permissions | null | undefined): string[] =>
  [...new Set([...(permissions?.boardRead || []), ...(permissions?.boardWrite || [])])];

/** No per-user shares to wait for since the cutover; kept for its callers. */
export function sharedBoardsPending(
  _fetchedIds: string[],
  _permissions: Permissions | null | undefined,
  _userId: string | null | undefined,
): boolean {
  return false;
}

export const useBoards = create<BoardsState>((set, get) => ({
  boards: [],
  isLoading: false,
  userBoards: [],
  sharedBoards: [],
  activeBoards: [],
  archivedUserBoards: [],
  archivedSharedBoards: [],
  lastDoc: null,
  hasMore: true,
  permissionsRef: null,
  initialFetchDone: false,
  sharedFetchDone: false,
  currentUserId: null,
  sharedBoardIds: [],

  setBoards: (boards) => set(state => ({
    boards,
    ...deriveBoardLists(boards, state),
  })),
  
  getNext: (firstPageOnly = false, pageSize = 15) => {
    const { isLoading, hasMore, lastDoc, currentUserId: userId } = get();
    
    if (isLoading || !hasMore || !userId) return;
    if (firstPageOnly && lastDoc) return;
    
    set({ isLoading: true });
    
    // Team workspace → fetch boards stamped with the firm id (all members'
    // team boards). Personal → unchanged owner-scoped fetch. A flagged team
    // (sharingV2) fetches only the boards the user can read; a cold load
    // waits for that team's flag first.
    const teamFirmId = getActiveDesignFirmId(userId);

    const generation = ++fetchGeneration;

    // A page can hold only archived or other-workspace boards, so keep paging
    // until a visible board lands or the query runs dry — otherwise the list
    // reads empty and no consumer ever asks for the next page.
    (async () => {
      try {
        const teamScope = teamScopeKnown(userId)
          ? activeTeamScope(userId)
          : await awaitActiveTeamScope(userId);
        if (generation !== fetchGeneration) return;
        let cursor = lastDoc;
        let more = true;
        // A first page replaces the list, so it starts from zero visible boards.
        const visibleBefore = lastDoc ? get().userBoards.length : 0;
        do {
          const pageCursor = cursor;
          const { boards: newBoards, lastVisible } = await getBoardsScheme(
            pageCursor, userId, pageSize, teamFirmId, teamScope
          );
          // A reset or a newer fetch (workspace switch) now owns the store.
          if (generation !== fetchGeneration) return;
          if (get().currentUserId !== userId) {
            // The new user's getNext returned early while this loop ran.
            set({ isLoading: false, lastDoc: null, hasMore: true });
            get().getNext(true, pageSize);
            return;
          }
          more = newBoards.length >= pageSize;
          set(state => {
            const updatedBoards = pageCursor
              ? [...state.boards, ...newBoards.filter(board =>
                  !state.boards.some(b => b.id === board.id))]
              : newBoards;

            return {
              hasMore: more,
              boards: updatedBoards,
              lastDoc: lastVisible,
              ...deriveBoardLists(updatedBoards, state),
            };
          });
          if (!pageCursor) flushPendingShared();
          cursor = lastVisible;
        } while (more && get().userBoards.length === visibleBefore);
        set({ isLoading: false });
      } catch (error) {
        console.error("Error fetching boards:", error);
        if (generation === fetchGeneration) {
          set({ isLoading: false });
          flushPendingShared();
        }
      }
    })();
  },
  
  // New method to fetch only shared boards when permissions change
  // Per-user (ACL) shares are gone since the cutover: a team list holds
  // everything shared with the user, and Personal is owner-only. The shared
  // lists stay empty until PR6 removes them.
  refreshSharedBoards: async (permissions: Permissions) => {
    skippedShared = null;
    // The ids are recorded so getBoards doesn't ask again on every call.
    set({ sharedBoards: [], archivedSharedBoards: [], sharedBoardIds: sharedBoardIdsOf(permissions), sharedFetchDone: true });
  },
  
  getBoards: (userId, permissions, pageSize = 15) => {
    const state = get();

    // Check if we need to fetch shared boards (permissions changed or not fetched yet)
    const currentSharedIds = sharedBoardIdsOf(permissions);
    const sharedIdsChanged = JSON.stringify(currentSharedIds.sort()) !== JSON.stringify(state.sharedBoardIds.sort());
    const needSharedFetch = !state.sharedFetchDone || sharedIdsChanged;

    // Handle permission changes for already initialized state
    if (state.initialFetchDone && needSharedFetch && permissions && userId === state.currentUserId) {
      refreshSharedAfterFirstPage(permissions);
    }
    
    // Initialize fetch if needed (first time or user change). Own boards
    // first; shared boards follow once page 1 has landed.
    if ((!state.initialFetchDone || userId !== state.currentUserId) && userId) {
      set({ currentUserId: userId, permissionsRef: permissions, lastDoc: null, hasMore: true });
      get().getNext(true, pageSize);
      set({ initialFetchDone: true });
      if (needSharedFetch && permissions) {
        refreshSharedAfterFirstPage(permissions);
      }
    }
    
    // Return current state
    return {
      boards: get().boards,
      activeBoards: get().activeBoards,
      userBoards: get().userBoards,
      sharedBoards: get().sharedBoards,
      setBoards: get().setBoards,
      getNext: () => get().getNext(false, pageSize),
      isLoading: get().isLoading,
      hasMore: get().hasMore
    };
  },

  getBoardsOptimized: (userId, permissions, pageSize = 15) => {
      const state = get();
      
      // Skip if no userId, or the initial load is already running. A later
      // permissions change still refreshes shared boards, after page 1.
      if (!userId || (state.isLoading && !state.initialFetchDone)) {
          return state; // Return current state as-is
      }

      // Case 1: Initial load - fetch user's own boards
      if (!state.initialFetchDone) {
          set({ 
              currentUserId: userId,
              permissionsRef: permissions
          });
          
          // Fetch user boards
          get().getNext(true, pageSize);
          set({ initialFetchDone: true });
          
          // Fetch shared boards if permissions available
          if (permissions) {
              refreshSharedAfterFirstPage(permissions);
          }
      } 
      // Case 2: Already initialized - check if we need to update shared boards
      else if (permissions && state.permissionsRef !== permissions) {
          set({ permissionsRef: permissions });
          refreshSharedAfterFirstPage(permissions);
      }
      
      // Return interface to current state
      return {
          boards: state.boards,
          activeBoards: state.activeBoards,
          userBoards: state.userBoards,
          sharedBoards: state.sharedBoards,
          setBoards: state.setBoards,
          getNext: () => get().getNext(false, pageSize),
          isLoading: state.isLoading,
          hasMore: state.hasMore
      };
  },

  syncBoardAssociations: (boardsToAdd: string[], boardsToRemove: string[], projectId: string) => {
    set(state => {
      const updateBoard = (board) => {
        if (boardsToAdd.includes(board.id)) {
          return { ...board, associatedProjectId: projectId };
        }
        if (boardsToRemove.includes(board.id)) {
          return { ...board, associatedProjectId: null };
        }
        return board;
      };

      // Apply the updateBoard function to all board arrays
      const updatedBoards = state.boards.map(updateBoard);
      const updatedSharedBoards = state.sharedBoards.map(updateBoard);
      const updatedArchivedSharedBoards = state.archivedSharedBoards.map(updateBoard);

      // Re-derive the lists (incl. activeBoards) so the team-workspace rules hold.
      return {
        boards: updatedBoards,
        ...deriveBoardLists(updatedBoards, { ...state, sharedBoards: updatedSharedBoards }),
        archivedSharedBoards: updatedArchivedSharedBoards,
      };
    });
  },

  patchBoard: (boardId, patch) => {
    set(state => {
      const apply = (list: BoardSchemeInterface[]) =>
        list.map(board => (board.id === boardId ? { ...board, ...patch } : board));
      return {
        boards: apply(state.boards),
        userBoards: apply(state.userBoards),
        sharedBoards: apply(state.sharedBoards),
        activeBoards: apply(state.activeBoards),
        archivedUserBoards: apply(state.archivedUserBoards),
        archivedSharedBoards: apply(state.archivedSharedBoards),
      };
    });
  },

  removeBoard: (boardId) => {
    set(state => {
      const keep = (list: BoardSchemeInterface[]) => list.filter(board => board.id !== boardId);
      const boards = keep(state.boards);
      return {
        boards,
        archivedSharedBoards: keep(state.archivedSharedBoards),
        ...deriveBoardLists(boards, { ...state, sharedBoards: keep(state.sharedBoards) }),
      };
    });
  },

  resetBoards: () => {
    fetchGeneration++; // drop any in-flight page loop
    pendingShared = null;
    skippedShared = null;
    set({
      boards: [],
      userBoards: [],
      sharedBoards: [],
      activeBoards: [],
      archivedUserBoards: [],
      archivedSharedBoards: [],
      isLoading: false,
      lastDoc: null,
      hasMore: true,
      initialFetchDone: false,
      sharedFetchDone: false,
      sharedBoardIds: []
    });
  }

}));

// When the active workspace changes, the team vs. personal query differs
// (owner-scoped vs design_firm_id-scoped), so the previously-fetched cache is
// the wrong set — a team needs teammates' boards that were never fetched.
// Reset the page window and refetch page 1 with the now-active query.
export const refetchFirstPage = () => {
  const s = useBoards.getState();
  if (!s.currentUserId || !s.initialFetchDone) return; // not active yet
  useBoards.setState({
    boards: [],
    userBoards: [],
    activeBoards: [],
    archivedUserBoards: [],
    lastDoc: null,
    hasMore: true,
    isLoading: false,
  });
  s.getNext(true);
  // Leaving a flagged team: fetch the ACL-shared boards it skipped.
  const skipped = skippedShared;
  if (skipped && skipped.uid === s.currentUserId && !activeTeamScope(s.currentUserId)) {
    refreshSharedAfterFirstPage(skipped.permissions);
  }
};

useWorkspaceStore.subscribe((state, prev) => {
  if (state.activeIdByUid === prev.activeIdByUid) return;
  refetchFirstPage();
});

// The team's sharingV2 flag can arrive after the first page (the firm doc
// loads alongside the list): the query shape changed, so refetch.
onSharingV2Flip(() => getActiveDesignFirmId(useBoards.getState().currentUserId), refetchFirstPage);

